import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AllianceStats } from '../types';
import { apiRequest } from '../lib/api';

const Stats: React.FC = () => {
  const [stats, setStats] = useState<AllianceStats>({
    members: 0,
    totalPower: 0,
    warsWon: 0,
    allianceLevel: 0,
  });

  // Fetch metrics from API
  const { data: metricsData, isLoading } = useQuery<AllianceStats>({
    queryKey: ['alliance-metrics'],
    queryFn: async () => {
      // Try to fetch from API endpoint
      // In dev mode, Vite proxy handles /api requests
      // In production, use VITE_API_URL or default to /api/metrics
      const apiUrl = (import.meta.env as { VITE_API_URL?: string }).VITE_API_URL || '/api/metrics';
      const url = `${apiUrl}?alliance=knki`;
      
      try {
        const data = await apiRequest<AllianceStats>(url);
        return data;
      } catch (error) {
        console.warn('Failed to fetch metrics, using defaults:', error);
        // Return default stats if API fails
        return {
          members: 0,
          totalPower: 0,
          warsWon: 0,
          allianceLevel: 0,
        };
      }
    },
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000, // Consider data stale after 30 seconds
    retry: 2, // Retry failed requests twice
  });

  // Update stats when metrics are fetched
  useEffect(() => {
    if (metricsData) {
      setStats({
        members: metricsData.members || 0,
        totalPower: metricsData.totalPower || 0,
        warsWon: metricsData.warsWon || 0,
        allianceLevel: metricsData.allianceLevel || 0,
        lastUpdated: metricsData.lastUpdated,
      });
    }
  }, [metricsData]);

  const statCardsRef = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
            const statValue = entry.target.querySelector('.stat-value') as HTMLElement;
            if (statValue) {
              const target = parseInt(statValue.getAttribute('data-target') || '0', 10);
              animateValue(statValue, target);
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    statCardsRef.current.forEach((card: HTMLDivElement | null) => {
      if (card) {
        card.classList.add('fade-in-ready');
        observer.observe(card);
      }
    });

    return () => {
      statCardsRef.current.forEach((card: HTMLDivElement | null) => {
        if (card) observer.unobserve(card);
      });
    };
  }, []);

  const animateValue = (element: HTMLElement, target: number, duration: number = 2000): void => {
    const start = 0;
    const increment = target / (duration / 16);
    let current = start;

    const updateValue = (): void => {
      current += increment;
      if (current < target) {
        element.textContent = Math.floor(current).toLocaleString();
        requestAnimationFrame(updateValue);
      } else {
        element.textContent = target.toLocaleString();
      }
    };

    updateValue();
  };

  const statItems = [
    { label: 'Members', value: stats.members, target: stats.members },
    { label: 'Total Power', value: stats.totalPower, target: stats.totalPower },
    { label: 'Wars Won', value: stats.warsWon, target: stats.warsWon },
    { label: 'Alliance Level', value: stats.allianceLevel, target: stats.allianceLevel },
  ];

  return (
    <section id="stats" className="section">
      <div className="container">
        <h2 className="section-title">Alliance Statistics</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-8">
          {statItems.map((stat, index) => (
            <div
              key={index}
              className="bg-bg-card p-10 rounded-xl border border-border text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-glow"
              ref={(el) => {
                if (el) statCardsRef.current[index] = el;
              }}
            >
              <div
                className="stat-value font-orbitron text-5xl font-black mb-2 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"
                data-target={stat.target}
              >
                {stat.value}
              </div>
              <div className="text-text-secondary text-lg uppercase tracking-wide">{stat.label}</div>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          {isLoading && (
            <p className="text-text-muted italic">Loading metrics...</p>
          )}
          {!isLoading && stats.lastUpdated && (
            <p className="text-text-muted text-sm">
              Last updated: {new Date(stats.lastUpdated).toLocaleString()}
            </p>
          )}
          {!isLoading && !stats.lastUpdated && (
            <p className="text-text-muted italic">
              *Stats are updated regularly. Connect with us for real-time data.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default Stats;
